import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartyPopper, Clock, Target, Mail, Calendar } from 'lucide-react';
import { Button, Card, LoadingSpinner } from '@/components/ui';
import { Logo } from '@/components/layout';
import { useAuth } from '@/contexts/AuthContext';
import { usePreferences } from '@/hooks/usePreferences';
import { api } from '@/lib/api';
import { format, addDays } from 'date-fns';
import type { DeliverySchedule } from '@/types';

export function ThankYouPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences, isLoading: prefsLoading } = usePreferences();
  const [trialEndDate, setTrialEndDate] = useState<string>('');
  const [deliverySchedules, setDeliverySchedules] = useState<DeliverySchedule[]>([]);

  useEffect(() => {
    // Fetch subscription to get trial end date
    api.getSubscriptionStatus().then((sub) => {
      if (sub.trial_end_date) {
        setTrialEndDate(format(new Date(sub.trial_end_date), 'MMMM dd, yyyy'));
      } else {
        setTrialEndDate(format(addDays(new Date(), 7), 'MMMM dd, yyyy'));
      }
    }).catch(() => {
      setTrialEndDate(format(addDays(new Date(), 7), 'MMMM dd, yyyy'));
    });

    // Fetch delivery schedules for displaying scheduled time
    api.getDeliverySchedules()
      .then((response) => setDeliverySchedules(response.schedules))
      .catch((err) => console.warn('Failed to fetch delivery schedules:', err));
  }, []);

  if (prefsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto text-center">
        <Logo size="lg" linkTo="/" />
        
        <div className="mt-12 mb-8">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <PartyPopper className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">You're all set!</h1>
          <p className="text-text-secondary">
            Your personalized newsletter is being prepared
          </p>
        </div>

        <Card className="mb-8 text-left">
          <h3 className="font-semibold mb-4 text-center">Your Newsletter Details</h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-surface-light rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Delivery Time</p>
                <p className="font-medium">
                  {(() => {
                    // Use delivery schedules if available, fall back to preferences
                    const activeSchedules = deliverySchedules.filter(s => s.is_active);
                    if (activeSchedules.length > 0) {
                      const schedule = activeSchedules[0];
                      const tz = schedule.timezone_abbr || schedule.timezone?.split('/')[1]?.replace('_', ' ') || '';
                      const extra = activeSchedules.length > 1 ? ` (+${activeSchedules.length - 1} more)` : '';
                      return `${schedule.delivery_time} (${tz})${extra}`;
                    }
                    return `${preferences?.delivery_time || '08:00'} (${preferences?.timezone_abbr || preferences?.timezone?.split('/')[1]?.replace('_', ' ') || 'New York'})`;
                  })()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-surface-light rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Topic</p>
                <p className="font-medium">
                  {preferences?.preset_name || preferences?.custom_prompt?.slice(0, 50) || 'Custom Prompt'}
                  {preferences?.custom_prompt && preferences.custom_prompt.length > 50 ? '...' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-surface-light rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
          </div>
        </Card>

        <div className="bg-surface-light border border-border rounded-lg p-4 mb-8">
          <div className="flex items-center justify-center gap-2 text-text-secondary">
            <Calendar className="w-4 h-4" />
            <span>Trial ends: {trialEndDate}</span>
          </div>
        </div>

        <Button size="lg" onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
}

